import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Newspaper, Plus, Send, CheckCircle, Globe, TrendingUp, TrendingDown,
  Minus, Loader2, ChevronDown, ChevronUp, Sparkles, Clock, Eye,
  FileText, AlertTriangle, ArrowUpRight, BarChart3, RefreshCw,
  Activity, DollarSign, Zap, Map, Building, Heart, Car, Home,
  Factory, Cpu, ShieldCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { usePermission } from '@/hooks/usePermission';
import { useRegion } from '@/context/RegionContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Helpers ────────────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  draft:      { label: 'Draft',       color: 'bg-[#f5f5f5] text-[#2a2a2a]' },
  in_review:  { label: 'In Review',   color: 'bg-amber-100 text-amber-700' },
  published:  { label: 'Published',   color: 'bg-green-100 text-green-700' },
  archived:   { label: 'Archived',    color: 'bg-[#f5f5f5] text-[#4a4a4a]' },
};

const VIEW_ICON = {
  overweight:  <TrendingUp className="w-3.5 h-3.5 text-green-500" />,
  neutral:     <Minus className="w-3.5 h-3.5 text-[#4a4a4a]" />,
  underweight: <TrendingDown className="w-3.5 h-3.5 text-red-500" />,
};

const VIEW_COLOR = {
  overweight:  'text-green-600 bg-green-50 border-green-200',
  neutral:     'text-[#4a4a4a] bg-[#f5f5f5] border-[#e5e5e5]',
  underweight: 'text-red-600 bg-red-50 border-red-200',
};

const REC_COLOR = {
  OVERWEIGHT: 'bg-green-100 text-green-800',
  NEUTRAL:    'bg-yellow-100 text-yellow-800',
  UNDERWEIGHT:'bg-red-100 text-red-800',
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cfg.color}`}>{cfg.label}</span>;
}

// ── Live Market Components ─────────────────────────────────────────────────────

function IndexCard({ label, value, change, changePct }) {
  const isPos = (changePct || change || 0) >= 0;
  const fmtVal = (v) => {
    if (!v && v !== 0) return '—';
    if (v >= 1000) return v.toLocaleString('en-US', { maximumFractionDigits: 0 });
    return v.toFixed(2);
  };
  return (
    <Card className={`border-l-4 ${isPos ? 'border-l-green-500' : 'border-l-red-500'}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-[#4a4a4a] mb-1">{label}</p>
            <p className="text-2xl font-bold text-[#1a1a1a]">{fmtVal(value)}</p>
            <div className={`flex items-center gap-1 mt-1 text-sm font-medium ${isPos ? 'text-emerald-600' : 'text-red-500'}`}>
              <span>{isPos ? '+' : ''}{(changePct || 0).toFixed(2)}%</span>
              {change !== undefined && (
                <span className="text-xs font-normal text-[#4a4a4a] ml-1">
                  ({isPos ? '+' : ''}{(change || 0).toFixed(2)})
                </span>
              )}
            </div>
          </div>
          {isPos
            ? <TrendingUp className="h-8 w-8 text-green-500 opacity-60" />
            : <TrendingDown className="h-8 w-8 text-red-500 opacity-60" />}
        </div>
      </CardContent>
    </Card>
  );
}

function SectorGrid({ sectors }) {
  if (!sectors?.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {sectors.map((sv, i) => (
        <div key={i} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border ${VIEW_COLOR[sv.view] || VIEW_COLOR.neutral}`}>
          {VIEW_ICON[sv.view] || VIEW_ICON.neutral}
          <span>{sv.sector}</span>
          <span className="opacity-60 capitalize">· {sv.view}</span>
        </div>
      ))}
    </div>
  );
}

function EconKPICard({ label, value, commentary, status }) {
  const statusColor = status === 'positive' ? 'border-l-emerald-400' : status === 'negative' ? 'border-l-red-400' : 'border-l-amber-400';
  return (
    <div className={`bg-white border border-[#e5e5e5] rounded-lg p-4 border-l-4 ${statusColor}`}>
      <p className="text-xs text-[#4a4a4a] font-medium mb-1">{label}</p>
      <p className="text-lg font-bold text-[#1a1a1a]">{value || '—'}</p>
      {commentary && <p className="text-xs text-[#4a4a4a] mt-1 leading-relaxed">{commentary}</p>}
    </div>
  );
}

// ── Overview tab components ────────────────────────────────────────────────────

function MarketCommentary({ outlook, isUS }) {
  const positioning = outlook?.investment_thesis?.recommended_positioning;
  const period = outlook?.period;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Globe className="h-5 w-5 text-[#1a1a1a]" />
          Market Commentary
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="bg-gradient-to-br from-[#f9f9f9] to-indigo-50 rounded-lg p-6 mb-4">
          <div className="flex justify-between items-start mb-3">
            <h3 className="font-bold text-lg text-gray-900">
              {period ? `Market Outlook — ${period}` : `Weekly Market Outlook — ${new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`}
            </h3>
          </div>
          {positioning ? (
            <p className="text-gray-700">{positioning}</p>
          ) : isUS ? (
            <>
              <p className="text-gray-700 mb-3">
                <strong>Key Highlights:</strong> US markets showed resilience led by Technology and Industrials.
                AI-driven capex cycle continues to support mega-cap earnings while energy and real estate lag.
              </p>
              <p className="text-gray-700 mb-3">
                <strong>Macro Context:</strong> Fed signals a data-dependent pause; core PCE remains sticky above 2% target.
                Strong labor market supports consumer spending but elevates higher-for-longer rate risk.
              </p>
              <p className="text-gray-700">
                <strong>Outlook:</strong> We maintain overweight US large-cap equities and investment-grade bonds.
                Selective exposure to AI infrastructure and healthcare innovation themes.
              </p>
            </>
          ) : (
            <>
              <p className="text-gray-700 mb-3">
                <strong>Key Highlights:</strong> Indian markets showed mixed performance amid global uncertainty.
                IT sector outperformed on strong earnings while banking stocks faced pressure from RBI policy concerns.
              </p>
              <p className="text-gray-700 mb-3">
                <strong>Global Context:</strong> Fed dovish stance supports emerging market flows. Crude oil volatility
                remains a key monitorable for Indian markets given geopolitical tensions.
              </p>
              <p className="text-gray-700">
                <strong>Outlook:</strong> We maintain cautiously optimistic stance with focus on quality large-caps
                and defensive positioning.
              </p>
            </>
          )}
        </div>
        <div className="flex gap-4 text-sm flex-wrap">
          {isUS ? (
            <>
              <div className="flex items-center gap-2"><div className="w-3 h-3 bg-green-500 rounded-full" /><span>Bullish: Technology, Industrials</span></div>
              <div className="flex items-center gap-2"><div className="w-3 h-3 bg-yellow-500 rounded-full" /><span>Neutral: Financials, Consumer</span></div>
              <div className="flex items-center gap-2"><div className="w-3 h-3 bg-red-500 rounded-full" /><span>Bearish: Energy, REITs</span></div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2"><div className="w-3 h-3 bg-green-500 rounded-full" /><span>Bullish: IT, Pharma</span></div>
              <div className="flex items-center gap-2"><div className="w-3 h-3 bg-yellow-500 rounded-full" /><span>Neutral: FMCG, Auto</span></div>
              <div className="flex items-center gap-2"><div className="w-3 h-3 bg-red-500 rounded-full" /><span>Bearish: Banking, Metals</span></div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function GlobalEvents({ outlook, isUS }) {
  // Use risks_to_watch from published thesis if available, else static
  const risksRaw = outlook?.status === 'published'
    ? (outlook?.investment_thesis?.risks_to_watch || [])
    : [];

  const staticEvents = isUS
    ? [
        { event: 'Federal Reserve Policy', impact: 'Positive', description: 'Fed signals data-dependent pause; markets pricing in 1–2 cuts by year-end', date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }), severity: 'High' },
        { event: 'AI Infrastructure Cycle', impact: 'Positive', description: 'Mega-cap tech capex continues to support earnings and broadens to industrials', date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }), severity: 'High' },
        { event: 'Geopolitical Tensions',   impact: 'Negative', description: 'Ongoing geopolitical uncertainty elevates safe-haven demand and energy price volatility', date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }), severity: 'Medium' },
      ]
    : [
        { event: 'RBI Monetary Policy',     impact: 'Positive', description: 'RBI signals accommodative stance with potential for rate cuts amid moderating inflation', date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }), severity: 'High' },
        { event: 'FII Flows Recovery',      impact: 'Positive', description: 'Positive FII inflows support equity markets as global risk appetite improves', date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }), severity: 'Medium' },
        { event: 'Crude Oil Volatility',    impact: 'Negative', description: 'Oil price uncertainty a key monitorable for current account and inflation trajectory', date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }), severity: 'High' },
      ];

  // Convert plain risk strings into event objects
  const events = risksRaw.length > 0
    ? risksRaw.slice(0, 4).map(r => ({
        event: r,
        impact: 'Mixed',
        description: r,
        date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        severity: 'Medium',
      }))
    : staticEvents;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Map className="h-5 w-5 text-[#1a1a1a]" />
          Global Events &amp; Market Impact
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {events.map((ev, i) => (
            <div key={i} className={`p-4 rounded-none border-l-4 ${
              ev.impact === 'Positive' ? 'border-green-500 bg-green-50'
              : ev.impact === 'Negative' ? 'border-red-500 bg-red-50'
              : 'border-yellow-500 bg-yellow-50'
            }`}>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-gray-900">{ev.event}</h4>
                    <span className={`text-xs px-2 py-0.5 rounded-none font-medium ${
                      ev.impact === 'Positive' ? 'bg-green-200 text-green-800'
                      : ev.impact === 'Negative' ? 'bg-red-200 text-red-800'
                      : 'bg-yellow-200 text-yellow-800'
                    }`}>
                      {ev.impact}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700">{ev.description}</p>
                </div>
                <div className="text-right ml-4 shrink-0">
                  <p className="text-xs text-gray-500">{ev.date}</p>
                  <p className={`text-xs font-medium ${ev.severity === 'High' ? 'text-red-600' : 'text-yellow-600'}`}>
                    {ev.severity} Impact
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function SectorCard({ name, icon: Icon, recommendation, performance, rationale }) {
  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Icon className="h-5 w-5 text-[#1a1a1a]" />
            <h3 className="font-semibold text-sm text-gray-900">{name}</h3>
          </div>
          <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${REC_COLOR[recommendation] || 'bg-[#f5f5f5] text-[#2a2a2a]'}`}>
            {recommendation === 'OVERWEIGHT' && <TrendingUp className="w-3 h-3" />}
            {recommendation === 'NEUTRAL' && <Minus className="w-3 h-3" />}
            {recommendation === 'UNDERWEIGHT' && <TrendingDown className="w-3 h-3" />}
            {recommendation}
          </span>
        </div>
        {performance && (
          <div className="mb-2">
            <div className={`text-lg font-bold ${performance.startsWith('+') ? 'text-green-600' : 'text-red-600'}`}>
              {performance}
            </div>
            <div className="text-xs text-gray-500">YTD Performance</div>
          </div>
        )}
        <p className="text-xs text-gray-600 line-clamp-2">{rationale}</p>
      </CardContent>
    </Card>
  );
}

// ── Outlook section cards ──────────────────────────────────────────────────────

function MacroCard({ section }) {
  const [open, setOpen] = useState(true);
  return (
    <Card className="border border-[#e5e5e5]">
      <CardHeader className="pb-2 cursor-pointer select-none" onClick={() => setOpen(o => !o)}>
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold text-[#2a2a2a] flex items-center gap-2">
            <Globe className="w-4 h-4 text-[#1a1a1a]" />
            Macro Outlook — <span className="capitalize">{section.region}</span>
            {section.ai_generated && (
              <span className="text-xs text-purple-500 flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> AI Draft
              </span>
            )}
          </CardTitle>
          {open ? <ChevronUp className="w-4 h-4 text-[#4a4a4a]" /> : <ChevronDown className="w-4 h-4 text-[#4a4a4a]" />}
        </div>
      </CardHeader>
      {open && (
        <CardContent className="space-y-3 text-sm">
          {section.gdp_outlook && (
            <div>
              <p className="text-xs font-medium text-[#4a4a4a] uppercase tracking-wide mb-1">GDP Outlook</p>
              <p className="text-[#2a2a2a]">{section.gdp_outlook}</p>
            </div>
          )}
          {section.inflation_outlook && (
            <div>
              <p className="text-xs font-medium text-[#4a4a4a] uppercase tracking-wide mb-1">Inflation</p>
              <p className="text-[#2a2a2a]">{section.inflation_outlook}</p>
            </div>
          )}
          {section.interest_rate_outlook && (
            <div>
              <p className="text-xs font-medium text-[#4a4a4a] uppercase tracking-wide mb-1">Interest Rates / Central Bank</p>
              <p className="text-[#2a2a2a]">{section.interest_rate_outlook}</p>
            </div>
          )}
          {section.currency_outlook && (
            <div>
              <p className="text-xs font-medium text-[#4a4a4a] uppercase tracking-wide mb-1">Currency Outlook</p>
              <p className="text-[#2a2a2a]">{section.currency_outlook}</p>
            </div>
          )}
          {section.geopolitical_risks && (
            <div>
              <p className="text-xs font-medium text-[#4a4a4a] uppercase tracking-wide mb-1">Geopolitical Risks</p>
              <p className="text-[#2a2a2a]">{section.geopolitical_risks}</p>
            </div>
          )}
          {section.key_indicators?.length > 0 && (
            <div>
              <p className="text-xs font-medium text-[#4a4a4a] uppercase tracking-wide mb-2">Key Indicators</p>
              <div className="grid grid-cols-2 gap-2">
                {section.key_indicators.map((ind, i) => (
                  <div key={i} className="bg-[#f9f9f9] rounded-none p-2 border border-[#e5e5e5]">
                    <p className="text-xs text-[#4a4a4a]">{ind.label}</p>
                    <p className="text-sm font-semibold text-[#1a1a1a]">{ind.value}</p>
                    {ind.commentary && <p className="text-xs text-[#4a4a4a] mt-0.5">{ind.commentary}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

function MicroCard({ section }) {
  const [open, setOpen] = useState(true);
  return (
    <Card className="border border-[#e5e5e5]">
      <CardHeader className="pb-2 cursor-pointer select-none" onClick={() => setOpen(o => !o)}>
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold text-[#2a2a2a] flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#1a1a1a]" />
            Sector & Asset Class Views — <span className="capitalize">{section.region}</span>
            {section.ai_generated && (
              <span className="text-xs text-purple-500 flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> AI Draft
              </span>
            )}
          </CardTitle>
          {open ? <ChevronUp className="w-4 h-4 text-[#4a4a4a]" /> : <ChevronDown className="w-4 h-4 text-[#4a4a4a]" />}
        </div>
      </CardHeader>
      {open && (
        <CardContent className="space-y-4 text-sm">
          {section.sector_views?.length > 0 && (
            <div>
              <p className="text-xs font-medium text-[#4a4a4a] uppercase tracking-wide mb-2">Sector Views</p>
              <SectorGrid sectors={section.sector_views} />
            </div>
          )}
          {section.asset_class_views?.length > 0 && (
            <div>
              <p className="text-xs font-medium text-[#4a4a4a] uppercase tracking-wide mb-2">Asset Class Views</p>
              <div className="space-y-2">
                {section.asset_class_views.map((av, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap border ${VIEW_COLOR[av.view] || VIEW_COLOR.neutral}`}>
                      {VIEW_ICON[av.view] || VIEW_ICON.neutral}
                      <span className="capitalize">{av.view}</span>
                    </div>
                    <div>
                      <p className="font-medium text-[#2a2a2a] capitalize">{(av.asset_class || '').replace('_', ' ')}</p>
                      {av.rationale && <p className="text-[#4a4a4a]">{av.rationale}</p>}
                    </div>
                    {av.recommended_allocation != null && (
                      <span className="ml-auto text-xs font-semibold text-[#1a1a1a] whitespace-nowrap">{av.recommended_allocation}%</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {section.earnings_outlook && (
            <div>
              <p className="text-xs font-medium text-[#4a4a4a] uppercase tracking-wide mb-1">Earnings Outlook</p>
              <p className="text-[#2a2a2a]">{section.earnings_outlook}</p>
            </div>
          )}
          {section.valuation_commentary && (
            <div>
              <p className="text-xs font-medium text-[#4a4a4a] uppercase tracking-wide mb-1">Valuations</p>
              <p className="text-[#2a2a2a]">{section.valuation_commentary}</p>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

function ThesisCard({ thesis }) {
  const [open, setOpen] = useState(true);
  if (!thesis) return null;
  return (
    <Card className="border border-[#e5e5e5]">
      <CardHeader className="pb-2 cursor-pointer select-none" onClick={() => setOpen(o => !o)}>
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold text-[#2a2a2a] flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#1a1a1a]" />
            Investment Thesis
            {thesis.ai_generated && (
              <span className="text-xs text-purple-500 flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> AI Draft
              </span>
            )}
          </CardTitle>
          {open ? <ChevronUp className="w-4 h-4 text-[#4a4a4a]" /> : <ChevronDown className="w-4 h-4 text-[#4a4a4a]" />}
        </div>
      </CardHeader>
      {open && (
        <CardContent className="space-y-4 text-sm">
          {thesis.recommended_positioning && (
            <div className="bg-[#f9f9f9] rounded-lg p-3 border border-[#e5e5e5]">
              <p className="text-xs font-medium text-[#1a1a1a] uppercase tracking-wide mb-1">Recommended Positioning</p>
              <p className="text-[#2a2a2a]">{thesis.recommended_positioning}</p>
            </div>
          )}
          {thesis.top_conviction_ideas?.length > 0 && (
            <div className="space-y-3">
              <p className="text-xs font-medium text-[#4a4a4a] uppercase tracking-wide">Top Conviction Ideas</p>
              {thesis.top_conviction_ideas.map((idea, i) => (
                <div key={i} className="border border-[#e5e5e5] rounded-lg p-3 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-semibold text-[#1a1a1a]">{idea.title}</p>
                    {idea.time_horizon && (
                      <span className="text-xs text-[#4a4a4a] whitespace-nowrap flex items-center gap-1">
                        <Clock className="w-3 h-3" />{idea.time_horizon}
                      </span>
                    )}
                  </div>
                  <p className="text-[#4a4a4a]">{idea.description}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {idea.bull_case && (
                      <div className="bg-green-50 rounded p-2">
                        <p className="text-xs font-medium text-green-700 mb-1">Bull Case</p>
                        <p className="text-xs text-[#4a4a4a]">{idea.bull_case}</p>
                      </div>
                    )}
                    {idea.bear_case && (
                      <div className="bg-red-50 rounded p-2">
                        <p className="text-xs font-medium text-red-700 mb-1">Bear Case</p>
                        <p className="text-xs text-[#4a4a4a]">{idea.bear_case}</p>
                      </div>
                    )}
                  </div>
                  {idea.key_risks?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {idea.key_risks.map((r, ri) => (
                        <span key={ri} className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />{r}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {thesis.risks_to_watch?.length > 0 && (
            <div>
              <p className="text-xs font-medium text-[#4a4a4a] uppercase tracking-wide mb-2">Risks to Watch</p>
              <ul className="space-y-1">
                {thesis.risks_to_watch.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-[#4a4a4a]">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />
                    {r}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

// ── Static sector data (region-aware) ─────────────────────────────────────────

const US_SECTORS = [
  { name: 'Technology',          icon: Cpu,         recommendation: 'OVERWEIGHT',  performance: '+14.2%', rationale: 'AI adoption and cloud growth driving continued outperformance' },
  { name: 'Healthcare',          icon: Heart,       recommendation: 'OVERWEIGHT',  performance: '+6.8%',  rationale: 'Biotech innovation and resilient earnings support thesis' },
  { name: 'Industrials',         icon: Factory,     recommendation: 'OVERWEIGHT',  performance: '+9.5%',  rationale: 'Infrastructure spending and reshoring drive structural demand' },
  { name: 'Financials',          icon: Building,    recommendation: 'NEUTRAL',     performance: '+3.1%',  rationale: 'Rate normalization mixed; select banks well-positioned' },
  { name: 'Consumer Disc.',      icon: Car,         recommendation: 'NEUTRAL',     performance: '+1.4%',  rationale: 'Consumer spending resilient but margin pressures emerging' },
  { name: 'Energy',              icon: Zap,         recommendation: 'UNDERWEIGHT', performance: '-2.3%',  rationale: 'Transition headwinds and supply uncertainty weigh on outlook' },
];

const IN_SECTORS = [
  { name: 'Information Technology', icon: Cpu,      recommendation: 'OVERWEIGHT',  performance: '+12.4%', rationale: 'Strong earnings, AI adoption driving growth' },
  { name: 'Pharmaceuticals',        icon: Heart,    recommendation: 'OVERWEIGHT',  performance: '+8.7%',  rationale: 'Robust export demand, new product launches' },
  { name: 'Automotive',             icon: Car,      recommendation: 'NEUTRAL',     performance: '+2.1%',  rationale: 'Mixed demand, EV transition challenges' },
  { name: 'FMCG',                   icon: Home,     recommendation: 'NEUTRAL',     performance: '+1.8%',  rationale: 'Stable demand, rural recovery slow' },
  { name: 'Banking & Financial',    icon: Building, recommendation: 'UNDERWEIGHT', performance: '-3.2%',  rationale: 'NPA concerns, margin pressure from rate cuts' },
  { name: 'Metals & Mining',        icon: Factory,  recommendation: 'UNDERWEIGHT', performance: '-5.6%',  rationale: 'China demand concerns, oversupply issues' },
];

const US_KPIS = [
  { label: 'GDP Growth',     value: '2.3%',  commentary: 'Resilient consumer & labor market', status: 'positive' },
  { label: 'Core PCE',       value: '2.8%',  commentary: 'Above Fed 2% target, sticky', status: 'negative' },
  { label: 'Fed Funds Rate', value: '5.25%', commentary: 'Higher-for-longer stance', status: 'neutral' },
  { label: 'USD Index',      value: '104.2', commentary: 'Dollar broadly stable', status: 'neutral' },
];

const IN_KPIS = [
  { label: 'GDP Growth',  value: '6.8%',   commentary: 'Domestic demand drives growth', status: 'positive' },
  { label: 'CPI Inflation',value: '5.1%',  commentary: 'Within RBI tolerance band', status: 'neutral' },
  { label: 'Repo Rate',   value: '6.50%',  commentary: 'Accommodative stance signaled', status: 'neutral' },
  { label: 'INR / USD',   value: '84.2',   commentary: 'Stable, RBI managing volatility', status: 'neutral' },
];

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function MarketOutlookPage() {
  const { user } = useAuth();
  const region = useRegion();
  const isUS = region?.isUS || false;
  const canCreate  = usePermission('market_outlook:create');
  const canPublish = usePermission('market_outlook:publish');

  const [activeTab, setActiveTab] = useState('overview');

  // Live market data
  const [liveData, setLiveData] = useState(null);
  const [liveLoading, setLiveLoading] = useState(true);
  const [liveLastUpdated, setLiveLastUpdated] = useState(null);
  const [liveRefreshing, setLiveRefreshing] = useState(false);

  // Outlook editorial
  const [outlook, setOutlook] = useState(null);
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newPeriod, setNewPeriod] = useState('');

  // ── Data fetching ────────────────────────────────────────────────────────────

  const fetchLiveData = useCallback(async (silent = false) => {
    if (!silent) setLiveLoading(true);
    else setLiveRefreshing(true);
    try {
      const res = await axios.get(`${API}/market-outlook/live-data`);
      setLiveData(res.data);
      setLiveLastUpdated(new Date());
    } catch {
      // keep previous data on error
    } finally {
      setLiveLoading(false);
      setLiveRefreshing(false);
    }
  }, []);

  const loadLatest = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/market-outlook/latest`);
      setOutlook(res.data);
    } catch {
      setOutlook(null);
    }
  }, []);

  const loadSummaries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/market-outlook/`);
      setSummaries(res.data || []);
    } catch {
      setSummaries([]);
    }
  }, []);

  useEffect(() => {
    fetchLiveData();
    Promise.all([loadLatest(), loadSummaries()]).finally(() => setLoading(false));
  }, [fetchLiveData, loadLatest, loadSummaries]);

  // Auto-refresh live data every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => fetchLiveData(true), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchLiveData]);

  // ── Outlook editorial actions ─────────────────────────────────────────────────

  const handleCreate = async () => {
    if (!newTitle.trim() || !newPeriod.trim()) {
      toast.error('Title and period are required');
      return;
    }
    try {
      const res = await axios.post(`${API}/market-outlook/`, { title: newTitle, period: newPeriod });
      setShowNewDialog(false);
      setNewTitle(''); setNewPeriod('');
      toast.success('Draft outlook created');
      await loadSummaries();
      setOutlook(res.data);
      setActiveTab('research');
    } catch {
      toast.error('Failed to create outlook');
    }
  };

  const handleGenerate = async () => {
    if (!outlook?.id) return;
    setGenerating(true);
    try {
      const regions = isUS ? ['global', 'us'] : ['global', 'india'];
      const res = await axios.post(`${API}/market-outlook/${outlook.id}/generate`, {
        outlook_id: outlook.id,
        regions,
        include_macro: true,
        include_micro: true,
        include_thesis: true,
      });
      setOutlook(res.data);
      toast.success('AI draft generated — please review before publishing');
    } catch {
      toast.error('AI generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmit = async () => {
    if (!outlook?.id) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/market-outlook/${outlook.id}/submit`);
      setOutlook(res.data);
      toast.success('Submitted for CIO review');
    } catch {
      toast.error('Failed to submit for review');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePublish = async () => {
    if (!outlook?.id) return;
    setPublishing(true);
    try {
      const res = await axios.post(`${API}/market-outlook/${outlook.id}/publish`);
      setOutlook(res.data);
      toast.success('Market outlook published');
      await loadSummaries();
    } catch {
      toast.error('Failed to publish outlook');
    } finally {
      setPublishing(false);
    }
  };

  const loadOutlookById = async (id) => {
    try {
      const res = await axios.get(`${API}/market-outlook/${id}`);
      setOutlook(res.data);
      setShowHistoryPanel(false);
    } catch {
      toast.error('Failed to load outlook');
    }
  };

  // Decide region-primary section order
  const macroSections = outlook?.macro_sections
    ? [...outlook.macro_sections].sort((a) => (a.region === region.marketOutlookPrimary ? -1 : 0))
    : [];
  const microSections = outlook?.micro_sections
    ? [...outlook.micro_sections].sort((a) => (a.region === region.marketOutlookPrimary ? -1 : 0))
    : [];

  // Flatten all sector views from published micro sections
  const publishedSectors = outlook?.status === 'published'
    ? (microSections.flatMap(s => s.sector_views || []))
    : [];
  // Key economic KPIs from macro sections
  const publishedKPIs = outlook?.status === 'published'
    ? (macroSections.flatMap(s => s.key_indicators || []))
    : [];

  // Static placeholder sectors and KPIs
  const staticSectors = isUS ? US_SECTORS : IN_SECTORS;
  const staticKPIs    = isUS ? US_KPIS    : IN_KPIS;

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#dae4c4] flex items-center justify-center shadow-sm">
            <Newspaper className="w-5 h-5 text-[#1a1a1a]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[#1a1a1a]">Market Outlook</h1>
            <p className="text-sm text-[#4a4a4a] mt-0.5">
              Live market data · Macro &amp; sector views · Investment thesis
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canCreate && (
            <Button size="sm" onClick={() => setShowNewDialog(true)} className="bg-[#ffd87a] hover:bg-[#ffd87a] text-black">
              <Plus className="w-4 h-4 mr-1.5" />
              New Draft
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-[#f5f5f5] p-1 rounded-xl w-fit">
        {[
          { id: 'overview',  label: 'Overview',            icon: <Activity className="w-3.5 h-3.5" /> },
          { id: 'sectors',   label: 'Sectors',             icon: <BarChart3 className="w-3.5 h-3.5" /> },
          { id: 'economic',  label: 'Economic',            icon: <DollarSign className="w-3.5 h-3.5" /> },
          { id: 'research',  label: 'Research & Outlook',  icon: <FileText className="w-3.5 h-3.5" /> },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-[#ffd879] text-black'
                : 'text-[#4a4a4a] hover:text-[#2a2a2a]'
            }`}
          >
            {tab.icon}{tab.label}
          </button>
        ))}
      </div>

      {/* ── TAB: Overview ───────────────────────────────────────────────────── */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Indices strip */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-[#2a2a2a] uppercase tracking-wide flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-[#1a1a1a]" />
                Market Indices
              </h2>
              <div className="flex items-center gap-2 text-xs text-[#4a4a4a]">
                {liveLastUpdated && (
                  <span>Updated {liveLastUpdated.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
                )}
                <button
                  onClick={() => fetchLiveData(true)}
                  disabled={liveRefreshing}
                  className="p-1 rounded hover:bg-[#f5f5f5] text-[#4a4a4a] hover:text-[#4a4a4a] transition-colors"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${liveRefreshing ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>
            {liveLoading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {[1,2,3,4].map(i => (
                  <div key={i} className="bg-white border border-[#e5e5e5] rounded-xl p-4 animate-pulse">
                    <div className="h-3 bg-[#e5e5e5] rounded w-16 mb-2" />
                    <div className="h-6 bg-[#e5e5e5] rounded w-24 mb-2" />
                    <div className="h-3 bg-[#e5e5e5] rounded w-12" />
                  </div>
                ))}
              </div>
            ) : liveData?.indices ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {Object.entries(liveData.indices).map(([label, idx]) => (
                  <IndexCard
                    key={label}
                    label={label}
                    value={idx.value}
                    change={idx.change}
                    changePct={idx.change_pct}
                  />
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#4a4a4a]">No market data available</p>
            )}
            {liveData?.data_source === 'fallback' && (
              <p className="text-xs text-amber-500 mt-2 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Showing reference values — live feed temporarily unavailable
              </p>
            )}
          </div>

          {/* Market Commentary */}
          <MarketCommentary outlook={outlook} isUS={isUS} />

          {/* Global Events */}
          <GlobalEvents outlook={outlook} isUS={isUS} />
        </div>
      )}

      {/* ── TAB: Sectors ─────────────────────────────────────────────────────── */}
      {activeTab === 'sectors' && (
        <div className="space-y-6">
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-[#2a2a2a] uppercase tracking-wide flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-[#1a1a1a]" />
                Sector Views
                {publishedSectors.length > 0 && (
                  <span className="text-xs font-normal text-[#4a4a4a] normal-case">from published outlook</span>
                )}
              </h2>
            </div>
            {publishedSectors.length > 0 ? (
              <div className="bg-white border border-[#e5e5e5] rounded-xl p-4 mb-4">
                <SectorGrid sectors={publishedSectors} />
              </div>
            ) : null}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {staticSectors.map((s, i) => (
                <SectorCard key={i} {...s} />
              ))}
            </div>
          </div>

          {/* Asset class views from published outlook */}
          {microSections.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-sm font-semibold text-[#2a2a2a] uppercase tracking-wide">
                Asset Class Views — Published Outlook
              </h2>
              {microSections.map((s, i) => <MicroCard key={i} section={s} />)}
            </div>
          )}
        </div>
      )}

      {/* ── TAB: Economic ────────────────────────────────────────────────────── */}
      {activeTab === 'economic' && (
        <div className="space-y-6">
          {/* Economic Indicators */}
          <div>
            <h2 className="text-sm font-semibold text-[#2a2a2a] uppercase tracking-wide mb-3 flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-[#1a1a1a]" />
              Economic Indicators
              {publishedKPIs.length > 0 && (
                <span className="text-xs font-normal text-[#4a4a4a] normal-case">from published outlook</span>
              )}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {(publishedKPIs.length > 0 ? publishedKPIs : staticKPIs).map((kpi, i) => (
                <EconKPICard
                  key={i}
                  label={kpi.label}
                  value={kpi.value}
                  commentary={kpi.commentary}
                  status={kpi.status}
                />
              ))}
            </div>
          </div>

          {/* Macro outlook sections from published */}
          {macroSections.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-sm font-semibold text-[#2a2a2a] uppercase tracking-wide">
                Macroeconomic Detail
              </h2>
              {macroSections.map((s, i) => <MacroCard key={i} section={s} />)}
            </div>
          )}

          {/* Conviction ideas */}
          {outlook?.status === 'published' && outlook?.investment_thesis?.top_conviction_ideas?.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-[#2a2a2a] uppercase tracking-wide mb-3 flex items-center gap-2">
                <Zap className="w-4 h-4 text-[#1a1a1a]" />
                Top Conviction Ideas
                <span className="text-xs font-normal text-[#4a4a4a] normal-case">CIO · {outlook.period}</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {outlook.investment_thesis.top_conviction_ideas.slice(0, 4).map((idea, i) => (
                  <div key={i} className="bg-white border border-[#e5e5e5] rounded-none p-4">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <p className="font-semibold text-[#1a1a1a] text-sm">{idea.title}</p>
                      {idea.time_horizon && (
                        <span className="text-xs text-[#4a4a4a] whitespace-nowrap flex items-center gap-1 shrink-0">
                          <Clock className="w-3 h-3" />{idea.time_horizon}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-[#4a4a4a] mb-3">{idea.description}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {idea.bull_case && (
                        <div className="bg-green-50 rounded p-2">
                          <p className="text-xs font-medium text-green-700 mb-0.5">Bull Case</p>
                          <p className="text-xs text-[#4a4a4a]">{idea.bull_case}</p>
                        </div>
                      )}
                      {idea.bear_case && (
                        <div className="bg-red-50 rounded p-2">
                          <p className="text-xs font-medium text-red-700 mb-0.5">Bear Case</p>
                          <p className="text-xs text-[#4a4a4a]">{idea.bear_case}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty state */}
          {!loading && publishedKPIs.length === 0 && !macroSections.length && (
            <div className="bg-white border border-dashed border-[#e5e5e5] rounded-xl p-8 text-center">
              <DollarSign className="w-8 h-8 text-[#4a4a4a] mx-auto mb-2" />
              <p className="text-sm text-[#4a4a4a] font-medium mb-1">Showing reference data</p>
              <p className="text-xs text-[#4a4a4a] mb-3">Publish a market outlook to see live economic indicators</p>
              {canCreate && (
                <Button size="sm" variant="outline" onClick={() => { setActiveTab('research'); setShowNewDialog(true); }}>
                  <Plus className="w-4 h-4 mr-1" /> Create Outlook Draft
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── TAB: Research & Outlook ─────────────────────────────────────────── */}
      {activeTab === 'research' && (
        <div className="space-y-4">
          {/* History toggle */}
          <div className="flex items-center justify-between">
            <Button
              variant="outline" size="sm"
              onClick={() => setShowHistoryPanel(s => !s)}
              className="text-[#4a4a4a]"
            >
              <Eye className="w-4 h-4 mr-1.5" />
              History ({summaries.length})
            </Button>
          </div>

          {/* History panel */}
          {showHistoryPanel && summaries.length > 0 && (
            <Card className="border border-[#e5e5e5]">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-[#2a2a2a]">Outlook History</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-1">
                  {summaries.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => loadOutlookById(s.id)}
                      className="w-full text-left flex items-center justify-between px-3 py-2 rounded-lg hover:bg-[#f9f9f9] transition-colors"
                    >
                      <div>
                        <p className="text-sm font-medium text-[#1a1a1a]">{s.title}</p>
                        <p className="text-xs text-[#4a4a4a]">{s.period}</p>
                      </div>
                      <StatusBadge status={s.status} />
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* No outlook state */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[#ffd87a]" />
            </div>
          ) : !outlook ? (
            <Card className="border-dashed border-2 border-[#e5e5e5]">
              <CardContent className="py-16 flex flex-col items-center gap-3 text-center">
                <Newspaper className="w-10 h-10 text-[#4a4a4a]" />
                <p className="text-[#4a4a4a] font-medium">No published market outlook yet</p>
                {canCreate && (
                  <Button size="sm" onClick={() => setShowNewDialog(true)} className="bg-[#ffd87a] hover:bg-[#ffd87a] text-black mt-2">
                    <Plus className="w-4 h-4 mr-1.5" />
                    Create First Draft
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {/* Outlook header bar */}
              <div className="flex items-center justify-between bg-white border border-[#e5e5e5] rounded-xl px-4 py-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-base font-semibold text-[#1a1a1a]">{outlook.title}</h2>
                    <StatusBadge status={outlook.status} />
                  </div>
                  <p className="text-xs text-[#4a4a4a] mt-0.5">
                    Period: {outlook.period}
                    {outlook.published_at && ` · Published ${new Date(outlook.published_at).toLocaleDateString()}`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {canCreate && outlook.status === 'draft' && (
                    <Button size="sm" variant="outline" onClick={handleGenerate} disabled={generating}>
                      {generating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5 text-purple-500" />}
                      {generating ? 'Generating…' : 'Generate AI Draft'}
                    </Button>
                  )}
                  {canCreate && outlook.status === 'draft' && (
                    <Button size="sm" variant="outline" onClick={handleSubmit} disabled={submitting}>
                      {submitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
                      Submit for Review
                    </Button>
                  )}
                  {canPublish && ['draft', 'in_review'].includes(outlook.status) && (
                    <Button size="sm" onClick={handlePublish} disabled={publishing}
                      className="bg-green-600 hover:bg-green-700 text-white">
                      {publishing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1.5" />}
                      Publish
                    </Button>
                  )}
                </div>
              </div>

              {/* AI draft notice */}
              {outlook.status !== 'published' && (outlook.macro_sections?.some(s => s.ai_generated) || outlook.micro_sections?.some(s => s.ai_generated)) && (
                <div className="flex items-start gap-2 bg-purple-50 border border-purple-100 rounded-lg px-4 py-3 text-sm text-purple-700">
                  <Sparkles className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>This outlook contains AI-generated sections (marked with the AI icon). Review carefully before publishing.</span>
                </div>
              )}

              {/* Sources */}
              {outlook.ai_draft_sources?.length > 0 && (
                <div className="text-xs text-[#4a4a4a] flex flex-wrap gap-2 items-center">
                  <span className="font-medium">Sources:</span>
                  {outlook.ai_draft_sources.slice(0, 5).map((src, i) => {
                    try {
                      return (
                        <a key={i} href={src} target="_blank" rel="noopener noreferrer"
                           className="flex items-center gap-1 text-[#ffd87a] hover:underline truncate max-w-[200px]">
                          {new URL(src).hostname} <ArrowUpRight className="w-3 h-3 shrink-0" />
                        </a>
                      );
                    } catch { return null; }
                  })}
                </div>
              )}

              {/* Macro sections */}
              {macroSections.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-[#4a4a4a] uppercase tracking-wide">Macroeconomic Outlook</h3>
                  {macroSections.map((s, i) => <MacroCard key={i} section={s} />)}
                </div>
              )}

              {/* Micro sections */}
              {microSections.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-[#4a4a4a] uppercase tracking-wide">Sector & Asset Class Views</h3>
                  {microSections.map((s, i) => <MicroCard key={i} section={s} />)}
                </div>
              )}

              {/* Investment thesis */}
              {outlook.investment_thesis && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-[#4a4a4a] uppercase tracking-wide">Investment Thesis</h3>
                  <ThesisCard thesis={outlook.investment_thesis} />
                </div>
              )}

              {/* Research notes */}
              {outlook.research_notes?.length > 0 && (
                <Card className="border border-[#e5e5e5]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-[#2a2a2a]">Research Notes</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {outlook.research_notes.map((note, i) => (
                      <div key={i} className="bg-[#f9f9f9] rounded-lg p-3 text-sm">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-[#2a2a2a]">{note.author_name}</span>
                          <span className="text-xs text-[#4a4a4a]">
                            {new Date(note.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-[#4a4a4a]">{note.content}</p>
                        {note.sources?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {note.sources.map((s, si) => (
                              <a key={si} href={s} target="_blank" rel="noopener noreferrer"
                                 className="text-xs text-[#ffd87a] hover:underline">{s}</a>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Empty draft */}
              {outlook.status === 'draft' && macroSections.length === 0 && microSections.length === 0 && canCreate && (
                <Card className="border-dashed border-2 border-[#e5e5e5]">
                  <CardContent className="py-12 flex flex-col items-center gap-3 text-center">
                    <Sparkles className="w-8 h-8 text-purple-300" />
                    <p className="text-[#4a4a4a] font-medium">Draft is empty</p>
                    <p className="text-sm text-[#4a4a4a]">Generate an AI draft to populate macro, sector, and thesis sections</p>
                    <Button size="sm" variant="outline" onClick={handleGenerate} disabled={generating}>
                      {generating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5 text-purple-500" />}
                      {generating ? 'Generating…' : 'Generate AI Draft'}
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      )}

      {/* New Draft Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Market Outlook Draft</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-sm font-medium text-[#2a2a2a] mb-1 block">Title</label>
              <input
                className="w-full border border-[#e5e5e5] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#ffd87a]"
                placeholder="e.g. Q2 2026 Market Outlook"
                value={newTitle}
                onChange={e => setNewTitle(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#2a2a2a] mb-1 block">Period</label>
              <input
                className="w-full border border-[#e5e5e5] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#ffd87a]"
                placeholder="e.g. April 2026 or Q2 2026"
                value={newPeriod}
                onChange={e => setNewPeriod(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate} className="bg-[#ffd87a] hover:bg-[#ffd87a] text-black">Create Draft</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
